// ═══════════════════════════════════════════════════════════════════════════
// Role Hierarchy — BuildMyShifts
// Defines role seniority, substitution rules, and hierarchy-aware logic
// for shift assignment and AI suggestions.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Default role seniority levels (higher = more senior)
 */
export const DEFAULT_ROLE_HIERARCHY = [
  { role: 'registered_nurse', level: 5, label: 'Registered Nurse', canSupervise: true },
  { role: 'senior_carer', level: 4, label: 'Senior Carer', canSupervise: true },
  { role: 'team_leader', level: 4, label: 'Team Leader', canSupervise: true },
  { role: 'shift_supervisor', level: 4, label: 'Shift Supervisor', canSupervise: true },
  { role: 'carer', level: 3, label: 'Carer', canSupervise: false },
  { role: 'support_worker', level: 3, label: 'Support Worker', canSupervise: false },
  { role: 'healthcare_assistant', level: 3, label: 'Healthcare Assistant', canSupervise: false },
  { role: 'security_officer', level: 3, label: 'Security Officer', canSupervise: false },
  { role: 'bartender', level: 3, label: 'Bartender', canSupervise: false },
  { role: 'chef', level: 3, label: 'Chef', canSupervise: false },
  { role: 'waiter', level: 2, label: 'Waiter/Waitress', canSupervise: false },
  { role: 'porter', level: 2, label: 'Porter', canSupervise: false },
  { role: 'cleaner', level: 2, label: 'Cleaner', canSupervise: false },
  { role: 'trainee', level: 1, label: 'Trainee', canSupervise: false },
  { role: 'volunteer', level: 0, label: 'Volunteer', canSupervise: false },
];

/**
 * Default substitution rules: which roles can cover which
 * Key = required role, values = roles that can substitute (in preference order)
 */
export const DEFAULT_SUBSTITUTION_RULES = {
  registered_nurse: [],
  senior_carer: ['registered_nurse'],
  team_leader: ['shift_supervisor', 'registered_nurse'],
  shift_supervisor: ['team_leader', 'registered_nurse'],
  carer: ['senior_carer', 'registered_nurse'],
  support_worker: ['carer', 'senior_carer'],
  healthcare_assistant: ['carer', 'senior_carer', 'registered_nurse'],
  security_officer: ['shift_supervisor'],
  bartender: ['team_leader'],
  chef: [],
  waiter: ['bartender', 'team_leader'],
  porter: ['support_worker', 'carer'],
  cleaner: ['porter', 'support_worker'],
  trainee: ['carer', 'support_worker', 'security_officer', 'waiter'],
  volunteer: ['trainee', 'carer', 'support_worker'],
};

/**
 * Get the seniority level of a role
 */
export function getRoleLevel(role, customHierarchy) {
  const hierarchy = customHierarchy || DEFAULT_ROLE_HIERARCHY;
  const entry = hierarchy.find(r => r.role === role);
  return entry ? entry.level : 1;
}

/**
 * Get role metadata (label, canSupervise, level)
 */
export function getRoleInfo(role, customHierarchy) {
  const hierarchy = customHierarchy || DEFAULT_ROLE_HIERARCHY;
  return hierarchy.find(r => r.role === role) || { role, level: 1, label: role, canSupervise: false };
}

/**
 * Check if staffRole can cover requiredRole (same level or above)
 */
export function canCoverRole(staffRole, requiredRole, customHierarchy) {
  if (staffRole === requiredRole) return true;
  const staffLevel = getRoleLevel(staffRole, customHierarchy);
  const requiredLevel = getRoleLevel(requiredRole, customHierarchy);
  return staffLevel >= requiredLevel;
}

/**
 * Check if a role is an approved substitute for the required role
 */
export function isApprovedSubstitute(staffRole, requiredRole, customSubstitutions) {
  if (staffRole === requiredRole) return true;
  const rules = customSubstitutions || DEFAULT_SUBSTITUTION_RULES;
  const approvedSubs = rules[requiredRole] || [];
  return approvedSubs.includes(staffRole);
}

/**
 * Get list of roles that can fill a position (exact match + approved substitutes)
 * Results sorted by preference (exact match first, then by seniority closeness)
 */
export function getEligibleRoles(requiredRole, customHierarchy, customSubstitutions) {
  const rules = customSubstitutions || DEFAULT_SUBSTITUTION_RULES;
  const hierarchy = customHierarchy || DEFAULT_ROLE_HIERARCHY;
  const requiredLevel = getRoleLevel(requiredRole, hierarchy);

  const eligible = [requiredRole];
  const subs = rules[requiredRole] || [];
  eligible.push(...subs);

  // Sort by closeness to required level (prefer same or slightly above)
  return [...new Set(eligible)].sort((a, b) => {
    if (a === requiredRole) return -1;
    if (b === requiredRole) return 1;
    const levelA = getRoleLevel(a, hierarchy);
    const levelB = getRoleLevel(b, hierarchy);
    return Math.abs(levelA - requiredLevel) - Math.abs(levelB - requiredLevel);
  });
}

/**
 * Filter a list of staff to those eligible for a required role
 */
export function filterEligibleStaff(staff, requiredRole, customHierarchy, customSubstitutions) {
  if (!requiredRole) return staff;
  
  const eligibleRoles = getEligibleRoles(requiredRole, customHierarchy, customSubstitutions);

  return staff
    .filter(s => {
      const staffRole = s.requiredRole || s.role || 'trainee';
      return eligibleRoles.includes(staffRole);
    })
    .sort((a, b) => {
      const roleA = a.requiredRole || a.role || 'trainee';
      const roleB = b.requiredRole || b.role || 'trainee';
      const idxA = eligibleRoles.indexOf(roleA);
      const idxB = eligibleRoles.indexOf(roleB);
      return idxA - idxB;
    });
}

/**
 * Check if a shift requires supervisory presence
 * and whether the current assignees include a supervisor
 */
export function checkSupervisoryRequirement(shiftAssignments, requiredSupervisorRoles, customHierarchy) {
  const hierarchy = customHierarchy || DEFAULT_ROLE_HIERARCHY;
  const supervisors = hierarchy.filter(r => r.canSupervise).map(r => r.role);
  const required = requiredSupervisorRoles || supervisors;

  const hasSupervisor = shiftAssignments.some(assignment => {
    const role = assignment.requiredRole || assignment.role;
    return required.includes(role);
  });

  return {
    supervisorRequired: true,
    hasSupervisor,
    missingRole: hasSupervisor ? null : 'supervisor',
    message: hasSupervisor ? null : 'No supervisor assigned — a senior staff member is needed',
  };
}

/**
 * Rank staff candidates for a shift based on role fit
 * Returns scored list: [{ staff, roleScore, isExactMatch, isSubstitute }]
 */
export function rankByRoleFit(candidates, requiredRole, customHierarchy, customSubstitutions) {
  if (!requiredRole) {
    return candidates.map(s => ({
      staff: s,
      roleScore: 50,
      isExactMatch: false,
      isSubstitute: false,
    }));
  }

  const hierarchy = customHierarchy || DEFAULT_ROLE_HIERARCHY;
  const requiredLevel = getRoleLevel(requiredRole, hierarchy);

  return candidates.map(s => {
    const staffRole = s.requiredRole || s.role || 'trainee';
    const staffLevel = getRoleLevel(staffRole, hierarchy);
    const isExact = staffRole === requiredRole;
    const isSub = !isExact && isApprovedSubstitute(staffRole, requiredRole, customSubstitutions);

    let roleScore = 0;
    if (isExact) {
      roleScore = 100;
    } else if (isSub) {
      // Slightly reduce score based on level difference
      const diff = Math.abs(staffLevel - requiredLevel);
      roleScore = Math.max(40, 85 - diff * 15);
    } else if (canCoverRole(staffRole, requiredRole, hierarchy)) {
      roleScore = 30;
    }

    return { staff: s, roleScore, isExactMatch: isExact, isSubstitute: isSub };
  }).sort((a, b) => b.roleScore - a.roleScore);
}

/**
 * Get all available roles (for dropdown selectors) with labels
 */
export function getAvailableRoles(customHierarchy) {
  const hierarchy = customHierarchy || DEFAULT_ROLE_HIERARCHY;
  return hierarchy.map(r => ({ value: r.role, label: r.label, level: r.level }));
}

const roleHierarchy = {
  DEFAULT_ROLE_HIERARCHY,
  DEFAULT_SUBSTITUTION_RULES,
  getRoleLevel,
  getRoleInfo,
  canCoverRole,
  isApprovedSubstitute,
  getEligibleRoles,
  filterEligibleStaff,
  checkSupervisoryRequirement,
  rankByRoleFit,
  getAvailableRoles,
};
export default roleHierarchy;
